import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Save, Loader2, Calculator, LineChart as LineIcon, Users, Zap, Activity, BarChart3 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TOOLS = [
    { value: "cost", label: "Maliyet", icon: Calculator },
    { value: "roi", label: "ROI", icon: LineIcon },
    { value: "staff", label: "Personel", icon: Users },
    { value: "energy", label: "Enerji", icon: Zap },
    { value: "occupancy", label: "Doluluk", icon: Activity },
];
const TOOL_LABELS = { cost: "Maliyet", roi: "ROI", staff: "Personel", energy: "Enerji", occupancy: "Doluluk" };

export default function AdminTools() {
    const [tab, setTab] = useState("cost");
    const [stats, setStats] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/calculator-usage/stats");
                setStats(data);
            } catch (_) {}
        })();
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-tools-page">
            <div>
                <h1 className="text-2xl font-bold text-dark-navy">Araç Yönetimi</h1>
                <p className="text-sm text-deep-blue/70">Hesaplayıcı formülleri, bölgesel katsayılar ve kullanım istatistikleri.</p>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {TOOLS.map((t) => {
                        const Icon = t.icon;
                        const data = stats.by_tool?.[t.value] || { count: 0, pdfs: 0 };
                        return (
                            <div key={t.value} className="lift-card bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-100 p-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">{t.label}</span>
                                    <Icon className="w-4 h-4 text-teal" />
                                </div>
                                <div className="mt-1 font-data text-2xl font-bold text-dark-navy">{data.count}</div>
                                <div className="text-[10px] text-deep-blue/60 mt-0.5">{data.pdfs} PDF indirme</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {stats && (
                <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3 inline-flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" /> Araç Kullanımı (30 gün)
                    </h3>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.trend || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#64748b" />
                                <YAxis tick={{ fontSize: 10 }} stroke="#64748b" />
                                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="cost" stackId="a" fill="#39c8c1" />
                                <Bar dataKey="roi" stackId="a" fill="#d6b06b" />
                                <Bar dataKey="staff" stackId="a" fill="#123b52" />
                                <Bar dataKey="energy" stackId="a" fill="#22c55e" />
                                <Bar dataKey="occupancy" stackId="a" fill="#0c2438" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Config tabs */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-light-bg">
                    {TOOLS.map((t) => (
                        <TabsTrigger
                            key={t.value}
                            value={t.value}
                            data-testid={`admin-tools-tab-${t.value}`}
                            className="data-[state=active]:bg-dark-navy data-[state=active]:text-white"
                        >
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {TOOLS.map((t) => (
                    <TabsContent key={t.value} value={t.value} className="pt-4">
                        <ToolConfigEditor toolType={t.value} />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}

function ToolConfigEditor({ toolType }) {
    const [config, setConfig] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formulasJson, setFormulasJson] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/calculator-config/${toolType}`);
                setConfig(data);
                setFormulasJson(JSON.stringify(data.formulas || {}, null, 2));
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [toolType]);

    const updateRegional = (idx, value) => {
        setConfig((p) => {
            const next = JSON.parse(JSON.stringify(p));
            next.regional_coefficients[idx].coefficient = parseFloat(value) || 1.0;
            return next;
        });
    };
    const updateStarMul = (idx, value) => {
        setConfig((p) => {
            const next = JSON.parse(JSON.stringify(p));
            next.star_multipliers[idx].multiplier = parseFloat(value) || 1.0;
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            let formulas;
            try {
                formulas = JSON.parse(formulasJson || "{}");
            } catch (_) {
                toast.error("Formüller geçerli JSON değil");
                setSaving(false);
                return;
            }
            const { data } = await api.put(`/admin/calculator-config/${toolType}`, {
                formulas,
                regional_coefficients: config.regional_coefficients,
                star_multipliers: config.star_multipliers,
            });
            setConfig(data);
            setFormulasJson(JSON.stringify(data.formulas || {}, null, 2));
            toast.success(`${TOOL_LABELS[toolType]} ayarları kaydedildi`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!config) return <div className="text-sm text-slate-500">Yükleniyor...</div>;

    return (
        <div className="space-y-5" data-testid={`tool-config-${toolType}`}>
            <div className="flex items-center justify-end">
                <button
                    onClick={save}
                    disabled={saving}
                    className="bg-dark-navy text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70"
                    data-testid={`tool-save-${toolType}`}
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Kaydet
                </button>
            </div>

            <Section title="Formüller (JSON)">
                <textarea
                    value={formulasJson}
                    onChange={(e) => setFormulasJson(e.target.value)}
                    rows={10}
                    className="w-full font-data text-xs px-3 py-2 rounded-md border border-slate-300 focus:border-teal outline-none bg-light-bg/30"
                    data-testid={`tool-formulas-${toolType}`}
                />
                <p className="mt-1 text-[11px] text-deep-blue/60">
                    JSON formatında düzenlenebilir. Anahtar/değer çiftleri hesap motorunda kullanılır.
                </p>
            </Section>

            <Section title="Yıldız Çarpanları">
                <div className="grid grid-cols-5 gap-3">
                    {(config.star_multipliers || []).map((sm, i) => (
                        <div key={i} className="bg-light-bg rounded-md p-3 text-center">
                            <div className="text-xs uppercase tracking-wider text-deep-blue/80 font-semibold">{sm.star} ★</div>
                            <input
                                type="number"
                                step="0.05"
                                value={sm.multiplier}
                                onChange={(e) => updateStarMul(i, e.target.value)}
                                className="mt-2 w-full px-2 py-1.5 rounded border border-slate-300 text-sm font-data text-center"
                                data-testid={`tool-star-${toolType}-${sm.star}`}
                            />
                        </div>
                    ))}
                </div>
            </Section>

            <Section title="Bölgesel Katsayılar (81 il)">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 max-h-[440px] overflow-y-auto pr-2 admin-scroll">
                    {(config.regional_coefficients || []).map((rc, i) => (
                        <div key={rc.city_code} className="rounded border border-slate-200 px-2 py-1.5 flex items-center justify-between gap-2">
                            <span className="text-xs font-data text-deep-blue/80 font-semibold">{rc.city_code}</span>
                            <input
                                type="number"
                                step="0.05"
                                value={rc.coefficient}
                                onChange={(e) => updateRegional(i, e.target.value)}
                                className="w-16 px-1.5 py-1 rounded border border-slate-200 text-xs font-data text-center"
                            />
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">{title}</h3>
            {children}
        </div>
    );
}
